import { useEffect } from "react";
import { X, Lock, BookOpen, WhatsappLogo, ChatCircleDots } from "@phosphor-icons/react";
import { CurriculumModule } from "./types";

const PUMBLE_DM_URL =
  "https://app.pumble.com/workspace/67f5356953833205f81b99b1/686377f41769c00fbb5e7135";

function buildWhatsappUrl(moduleName: string): string {
  const message = `Merhaba Tunca Hoca, ${moduleName} modülü beni özellikle ilgilendiriyor, programın tamamı hakkında konuşmak istiyorum.`;
  return "https://wa.me/905323048683?text=" + encodeURIComponent(message);
}

export default function LockedModulePreviewModal({
  mod,
  onClose,
}: {
  mod: CurriculumModule;
  onClose: () => void;
}) {
  // ESC tuşu kapatır
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Body scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const whatsappUrl = buildWhatsappUrl(mod.name);
  const hasSkills = mod.skills.length > 0;
  const hasUnits = mod.unit_names.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-br from-[#0d1117] to-[#1a1110] border border-myAmber/30 rounded-2xl p-6 max-h-[80vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          onClick={onClose}
          aria-label="Kapat"
          className="absolute top-3 right-3 text-white/30 hover:text-white/70"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-myAmber/15 text-myAmber grid place-items-center">
            <Lock size={20} weight="fill" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40">
              Modül {mod.order} · Kilitli
            </div>
            <div className="text-white font-bold text-base leading-tight">{mod.name}</div>
          </div>
        </div>

        {hasSkills && (
          <>
            <div className="text-white/80 text-sm mb-2">Bu modülde öğreneceksin:</div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {mod.skills.map((s) => (
                <span
                  key={s}
                  className="text-[11px] px-2 py-1 rounded-md bg-myAmber/10 text-myAmber border border-myAmber/25"
                >
                  {s}
                </span>
              ))}
            </div>
          </>
        )}

        {hasUnits && (
          <>
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">
              Dersler
            </div>
            <ul className="space-y-1 mb-4">
              {mod.unit_names.map((u) => (
                <li
                  key={u}
                  className="text-white/80 text-xs flex items-start gap-2 py-1 border-b border-white/5 last:border-0"
                >
                  <BookOpen size={12} className="text-white/40 mt-0.5 shrink-0" />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="text-white/60 text-xs mb-3 leading-relaxed">
          Bu modüle erişmek için tam programa kayıt olman gerek. Tunca Hoca seni yönlendirir.
        </div>

        <div className="flex flex-wrap gap-2">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-[#25D366] text-black font-semibold text-xs px-3 py-2 rounded-lg hover:opacity-90"
          >
            <WhatsappLogo size={14} weight="fill" /> WhatsApp ile Yaz
          </a>
          <a
            href={PUMBLE_DM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-[#5B47E5] text-white font-semibold text-xs px-3 py-2 rounded-lg hover:opacity-90"
          >
            <ChatCircleDots size={14} weight="fill" /> Pumble'da Yaz
          </a>
        </div>
      </div>
    </div>
  );
}
